import { useCallback, useEffect, useRef } from 'react';
import { COOKIE_KEY, cookieUtils } from '@app/app/cookie';
import { addBreadcrumb, captureException, captureMessage } from '@sentry/react';
import * as Sentry from '@sentry/react';
import { usePersistedAccountsStore } from '@app/app/persist';

// import { PlatformName } from './types';
import { analytics, signUpAmplitude } from './analytics';

// Функция для безопасного форматирования user_id для Amplitude
const formatUserIdForAmplitude = (userId: string | null | undefined): string | undefined => {
  if (!userId || userId.length === 0) {
    return undefined; // Не передаваем пустой ID
  }

  // Amplitude требует минимум 5 символов
  if (userId.length < 5) {
    return `user_${userId}`; // Добавляем префикс: "9448" → "user_9448"
  }

  return userId;
};

export const handleRegisterByGoogle = (sendMarketingEmails: 'allow' | 'deny') => {
  cookieUtils.set(COOKIE_KEY.sendMarketingEmail, sendMarketingEmails, {
    path: '/',
    expires: undefined,
  });
};

export const trackOAuthAnalytics = (accountsOnDevice: number, userId: string) => {
  if (!userId || userId === 'undefined' || userId === 'null') {
    console.error('trackOAuthAnalytics: Invalid userId', { userId, accountsOnDevice });
    captureMessage('trackOAuthAnalytics: Invalid userId', 'error');
    return;
  }

  const isOAuthReg = cookieUtils.get(COOKIE_KEY.oAuthReg);
  const isOAuthLog = cookieUtils.get(COOKIE_KEY.oAuthLog);
  const sendMarketingEmails = cookieUtils.get(COOKIE_KEY.sendMarketingEmail);

  console.group('🔍 OAuth Analytics Debug');
  console.log('isOAuthReg', isOAuthReg);
  console.log('isOAuthLog', isOAuthLog);
  console.log('sendMarketingEmails', sendMarketingEmails);
  console.log('accountsOnDevice', accountsOnDevice);
  console.log('userId', userId);
  console.log('timestamp', new Date().toISOString());
  console.groupEnd();

  if (!isOAuthReg && !isOAuthLog) {
    console.warn('trackOAuthAnalytics: No OAuth cookies found');
    captureMessage('trackOAuthAnalytics: No OAuth cookies found', 'warning');
    return;
  }

  try {
    if (isOAuthReg) {
      if (typeof analytics?.emitEvent !== 'function') {
        console.error('Analytics GTM not ready for sign_up event');
        captureMessage('Analytics GTM not ready for sign_up event', 'error');
        return;
      }

      // Это пока добавили на форму регистрации
      // analytics.emitEvent('sign_up', {
      //   [PlatformName.GTM]: {
      //     accounts_on_device: accountsOnDevice,
      //     method: 'google',
      //     send_marketing_email: sendMarketingEmails,
      //   },
      // });

      if (typeof signUpAmplitude?.track !== 'function') {
        console.error('Amplitude not ready for sign up event');
        captureMessage('Amplitude not ready for sign up event', 'error');
      } else {
        signUpAmplitude.track('sign up trackOAuthAnalytics google', {
          method: 'google',
          user_id: userId,
        });
      }

      captureMessage(`Event sent: sign_up Method: google UserID: ${userId}`, 'info');

      cookieUtils.remove(COOKIE_KEY.oAuthReg);
      cookieUtils.remove(COOKIE_KEY.sendMarketingEmail);
    }

    if (isOAuthLog) {
      if (typeof analytics?.emitEvent !== 'function') {
        console.error('Analytics GTM not ready for login event');
        captureMessage('Analytics GTM not ready for login event', 'error');
        return;
      }

      // Это пока добавили на форму входа
      // analytics.emitEvent('login', {
      //   [PlatformName.GTM]: {
      //     method: 'google',
      //   },
      // });

      if (typeof signUpAmplitude?.track !== 'function') {
        console.error('Amplitude not ready for login event');
        captureMessage('Amplitude not ready for login event', 'error');
      } else {
        signUpAmplitude.track('login trackOAuthAnalytics', { method: 'google', user_id: userId });
      }

      captureMessage(`Event sent: login Method: google UserID: ${userId}`, 'info');

      cookieUtils.remove(COOKIE_KEY.oAuthLog);
    }
  } catch (e) {
    console.error('trackOAuthAnalytics error:', e);
    if (e instanceof Error) {
      Sentry.addBreadcrumb({
        message: e.message,
        data: {
          error: e,
          userId,
          accountsOnDevice,
          isOAuthReg: !!isOAuthReg,
          isOAuthLog: !!isOAuthLog,
        },
      });
      Sentry.captureException('Error on oauth analytics tracking');
    }
  }
};

export const trackOAuthAnalyticsWithWait = (accountsOnDevice: number, userId: string) => {
  if (!userId || userId === 'undefined' || userId === 'null') {
    console.error('trackOAuthAnalyticsWithWait: Invalid userId', { userId, accountsOnDevice });
    captureMessage('trackOAuthAnalyticsWithWait: Invalid userId', 'error');
    return;
  }

  const isOAuthReg = cookieUtils.get(COOKIE_KEY.oAuthReg);
  const isOAuthLog = cookieUtils.get(COOKIE_KEY.oAuthLog);
  const sendMarketingEmails = cookieUtils.get(COOKIE_KEY.sendMarketingEmail);

  // 🍪 Получаем все куки для отладки
  const allCookies = cookieUtils.getAll({});

  Sentry.addBreadcrumb({
    message: 'OAuth Analytics With Wait - Full Cookie Context',
    level: 'info',
    category: 'oauth.analytics.cookies',
    data: {
      userId,
      accountsOnDevice,
      isOAuthReg: !!isOAuthReg,
      isOAuthLog: !!isOAuthLog,
      sendMarketingEmails,
      allCookies,
      rawCookieString: document.cookie,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      referrer: document.referrer,
      pageUrl: window.location.href,
    },
  });

  console.group('🔍 OAuth Analytics With Wait Debug');
  console.log('isOAuthReg', isOAuthReg);
  console.log('isOAuthLog', isOAuthLog);
  console.log('sendMarketingEmails', sendMarketingEmails);
  console.log('accountsOnDevice', accountsOnDevice);
  console.log('userId', userId);
  console.log('allCookies', allCookies);
  console.log('rawCookieString', document.cookie);
  console.log('timestamp', new Date().toISOString());
  console.groupEnd();

  if (!isOAuthReg && !isOAuthLog) {
    console.warn('trackOAuthAnalyticsWithWait: No OAuth cookies found');

    // Используем withScope для передачи дополнительных данных
    Sentry.withScope((scope) => {
      scope.setExtra('allCookies', allCookies);
      scope.setExtra('rawCookieString', document.cookie);
      scope.setExtra('userId', userId);
      scope.setExtra('accountsOnDevice', accountsOnDevice);
      captureMessage('trackOAuthAnalyticsWithWait: No OAuth cookies found', 'warning');
    });
    return;
  }

  try {
    if (isOAuthReg) {
      // Это делаем при клике на кнопку в форме регистрации
      // analytics.emitEvent('sign_up', {
      //   [PlatformName.GTM]: {
      //     accounts_on_device: accountsOnDevice,
      //     method: 'google',
      //     send_marketing_email: sendMarketingEmails,
      //   },
      // });

      if (typeof signUpAmplitude?.track === 'function') {
        signUpAmplitude.track('sign up v2', { method: 'google', user_id: userId });
      } else {
        console.error('Amplitude not ready for sign up event');
        captureMessage('Amplitude not ready for sign up event', 'error');
      }

      captureMessage(`Event sent with wait: sign_up Method: google UserID: ${userId}`, 'info');

      cookieUtils.remove(COOKIE_KEY.oAuthReg);
      cookieUtils.remove(COOKIE_KEY.sendMarketingEmail);
    }

    if (isOAuthLog) {
      // Это делаем при клике на кнопку в форме входа
      // analytics.emitEvent('login', {
      //   [PlatformName.GTM]: {
      //     method: 'google',
      //   },
      // });

      if (typeof signUpAmplitude?.track === 'function') {
        signUpAmplitude.track('login', { method: 'google', user_id: userId });
      } else {
        console.error('Amplitude not ready for login event');
        captureMessage('Amplitude not ready for login event', 'error');
      }

      captureMessage(`Event sent with wait: login Method: google UserID: ${userId}`, 'info');

      cookieUtils.remove(COOKIE_KEY.oAuthLog);
    }
  } catch (e) {
    console.error('trackOAuthAnalyticsWithWait error:', e);
    if (e instanceof Error) {
      addBreadcrumb({
        message: e.message,
        data: {
          error: e,
          userId,
          accountsOnDevice,
          isOAuthReg: !!isOAuthReg,
          isOAuthLog: !!isOAuthLog,
        },
      });
      Sentry.captureException('Error on oauth analytics tracking with wait');
    }
  }
};

/**
 * Кастомный хук для OAuth аналитики
 * Использует один раз при появлении userId
 */
export const useOAuthAnalytics = (userId: string | number | undefined) => {
  const { accounts } = usePersistedAccountsStore();
  const processedUserIds = useRef(new Set<string>());

  const trackOAuth = useCallback(() => {
    if (!userId) return;

    const userIdStr = String(userId);

    // Предотвращаем повторную отправку для того же пользователя
    if (processedUserIds.current.has(userIdStr)) {
      console.log('🔄 OAuth Analytics: already processed for user', userIdStr);
      return;
    }

    // Проверяем наличие OAuth куки
    const keys = [COOKIE_KEY.oAuthLog, COOKIE_KEY.oAuthReg];
    const shouldTrack = keys.some((key) => cookieUtils.get(key));

    if (shouldTrack) {
      console.log('🎯 OAuth Analytics triggered from useOAuthAnalytics', {
        userId: userIdStr,
        accountsSize: accounts.size,
        hasOAuthReg: !!cookieUtils.get(COOKIE_KEY.oAuthReg),
        hasOAuthLog: !!cookieUtils.get(COOKIE_KEY.oAuthLog),
      });

      // Добавляем пользователя в обработанные ДО отправки
      processedUserIds.current.add(userIdStr);

      // Отправляем аналитику
      trackOAuthAnalyticsWithWait(accounts.size, userIdStr);
    }
  }, [userId, accounts.size]);

  useEffect(() => {
    trackOAuth();
  }, [trackOAuth]);

  return { trackOAuth };
};
